/**
 * Centralized Layout Spacing Constants
 *
 * These constants ensure consistent spacing across the application.
 * Use these instead of hardcoded values for padding, gaps, and dimensions.
 *
 * Usage:
 * ```tsx
 * import { LAYOUT_SPACING } from "~/components/layout/shared/spacing";
 *
 * <Box p={LAYOUT_SPACING.PAGE_PADDING}>
 *   <VStack gap={LAYOUT_SPACING.SECTION_GAP}>
 *     ...
 *   </VStack>
 * </Box>
 * ```
 */

export const LAYOUT_SPACING = {
  // Header
  HEADER_HEIGHT: 73, // px - App header height (used for calc(100vh - HEADER_HEIGHT))

  // Page-level spacing
  PAGE_PADDING: 6, // 24px - Outer page padding
  PAGE_PADDING_SM: 4, // 16px - Smaller page padding (mobile, compact views)

  // Section spacing
  SECTION_GAP: 6, // 24px - Gap between major sections (forms, tables, etc.)
  CONTENT_GAP: 4, // 16px - Gap within sections (between form fields, list items)
  ELEMENT_GAP: 3, // 12px - Gap between small elements (icons, badges, inline items)

  // Button spacing
  BUTTON_GAP: 3, // 12px - Gap between buttons in button groups
  BUTTON_GAP_LG: 3, // 12px - Larger gap between button groups

  // Card/Container spacing
  CARD_PADDING_SM: 3, // 12px - Smaller padding for compact cards
  CARD_PADDING: 4, // 16px - Padding inside cards and containers
  CARD_PADDING_LG: 6, // 24px - Larger padding for major cards

  // Content max widths
  MAX_WIDTH_FORM: '600px', // Simple forms and modals (e.g., CreateProjectModal)
  MAX_WIDTH_CONTENT: '1200px', // Complex multi-section pages (e.g., ProjectSetupPage)

  // Sidebar dimensions
  SIDEBAR_COLLAPSED: 56, // px - Collapsed sidebar width (icon/badge mode)
  SIDEBAR_EXPANDED_SM: 250, // px - Standard expanded sidebar width
  SIDEBAR_EXPANDED_MD: 300, // px - Larger expanded sidebar width
  SIDEBAR_EXPANDED_LG: 350, // px - Extra large expanded sidebar width

  // Drawer dimensions
  DRAWER_COLLAPSED: 56, // px - Collapsed drawer height
  DRAWER_PREVIEW: 200, // px - Preview drawer height
  DRAWER_FULL_MIN: 300, // px - Minimum full drawer height
  DRAWER_FULL_MAX: 600, // px - Maximum full drawer height

  // Table dimensions
  TABLE_DEFAULT_ROWS: 10, // Default number of rows to show in collapsed table view

  // Border radius
  BORDER_RADIUS: 'md', // Chakra token - Standard border radius
  BORDER_RADIUS_LG: 'lg', // Chakra token - Larger border radius

  // Transition durations (in CSS format)
  TRANSITION_FAST: '0.2s ease-in-out', // Quick transitions (drawers, hover states)
  TRANSITION_NORMAL: '0.3s ease-in-out', // Normal transitions (sidebar collapse)
  TRANSITION_SLOW: '0.4s ease-in-out', // Slow transitions (major layout changes)
} as const;

/**
 * Type for layout spacing keys
 */
export type LayoutSpacingKey = keyof typeof LAYOUT_SPACING;

/**
 * Helper function to get spacing value
 * Useful for computed/dynamic spacing
 */
export const getSpacing = (key: LayoutSpacingKey) => LAYOUT_SPACING[key];
